// EmailEditor.js
import React, { useState, useEffect, useRef, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Tooltip } from "react-tippy";
import "react-tippy/dist/tippy.css";
import { getTrans as t } from "../../Localization/translations";
import EmailTemplateService from "../../services/email/emailServices";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

export default function EmailEditor() {
  const location = useLocation();
  const navigate = useNavigate();
  const queryParams = new URLSearchParams(location.search);
  const templateName = queryParams.get("id") || queryParams.get("name");

  // Loading & meta
  const [loadingVariables, setLoadingVariables] = useState(true);
  const [hasVariables, setHasVariables] = useState(false);
  const [isOverride, setIsOverride] = useState(false);

  // Template fields
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [templateDesc, setTemplateDesc] = useState("");
  const [category, setCategory] = useState("access");
  const [isActive, setIsActive] = useState(true);

  // Variables
  const [variables, setVariables] = useState([]);
  const [variableKeyToId, setVariableKeyToId] = useState({});

  // Send/test
  const [testEmail, setTestEmail] = useState("");
  const [invoiceNo, setInvoiceNo] = useState("");
  const [partyName, setPartyName] = useState("");
  const [ticketId, setTicketId] = useState("");

  // Focus + refs
  const [focusedField, setFocusedField] = useState(null);
  const subjectRef = useRef(null);
  const bodyRef = useRef(null);

  // show hide
  const lowerName = templateName?.toLowerCase() || "";
  const isInvoiceTemplate =
    lowerName.includes("past due") || lowerName.includes("due date") || lowerName.includes("invoice");
  const isPasswordTemplate = templateName?.toLowerCase().includes("password");
  const supportTemplate = templateName?.toLowerCase().includes("support");


  // -----------------------
  // Helpers (formatting)
  // -----------------------
  const htmlToText = useCallback((html) => {
    if (!html) return "";
    return html
      .replace(/<br\s*\/?>/gi, "\n") // <br> → newline
      .replace(/<\/p>\s*<p>/gi, "\n\n") // paragraphs -> double newline
      .replace(/<\/?p[^>]*>/gi, "\n") // p tags -> newline
      .replace(/<\/?div[^>]*>/gi, "\n") // div -> newline
      .replace(/<[^>]+>/g, "") // remove remaining tags
      .replace(/&nbsp;/g, " ")
      .replace(/&amp;/g, "&")
      .replace(/&lt;/g, "<")
      .replace(/&gt;/g, ">")
      .replace(/\n{3,}/g, "\n\n")
      .trim();
  }, []);

  const textToHtml = useCallback((text) => {
    if (!text) return "";
    // Convert paragraphs: double newline => separate paragraphs
    const content = text
      .replace(/\r\n/g, "\n")
      .replace(/\n{2,}/g, "</p><p>")
      .replace(/\n/g, "<br>");
    return `<div>${content}</div>`;
  }, []);

  const convertToHtmlForPreview = useCallback((text) => {
    if (!text) return "";
    // escape HTML and then convert newlines
    const escapeHtml = (s) =>
      s
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;");
    const escaped = escapeHtml(text);
    const withParagraphs = escaped
      .replace(/\n{2,}/g, "</p><p>")
      .replace(/\n/g, "<br>");
    return `<p>${withParagraphs}</p>`;
  }, []);

  // -----------------------
  // Cursor insertion helper
  // -----------------------
  const insertAtCursor = (ref, setter, text) => {
    const el = ref.current;
    if (!el) {
      // fallback append
      setter((prev) => prev + text);
      return;
    }

    const start = el.selectionStart ?? el.value.length;
    const end = el.selectionEnd ?? el.value.length;

    setter((prev) => {
      return prev.slice(0, start) + text + prev.slice(end);
    });

    // set cursor after text
    requestAnimationFrame(() => {
      el.focus();
      const pos = start + text.length;
      try {
        el.setSelectionRange(pos, pos);
      } catch (e) {
        // ignore if cannot set
      }
    });
  };

  const handleInsert = (key) => {
    const placeholder = `{{${key}}}`;
    if (focusedField === "subject") {
      insertAtCursor(subjectRef, setSubject, placeholder);
    } else {
      insertAtCursor(bodyRef, setBody, placeholder);
    }
  };

  const handleCopy = (key) => {
    try {
      navigator.clipboard.writeText(`{{${key}}}`);
      toast.success("Copied to clipboard", { autoClose: 1500 });
    } catch (e) {
      toast.error("Copy failed", { autoClose: 2000 });
    }
  };

  // -----------------------
  // Load variables + template override (single flow)
  // -----------------------
  useEffect(() => {
    let mounted = true;
    setLoadingVariables(true);

    const loadAll = async () => {
      try {
        // Parallel: variables + override/template
        const varsPromise = EmailTemplateService.getVariables();
        let overridePromise = null;
        let templatePromise = null;

        if (templateName) {
          overridePromise = EmailTemplateService.getCompanyOverride(templateName).catch(
            (err) => {
              // if 404, then fallback to global
              if (err?.response?.status === 404) {
                return null;
              }
              throw err;
            }
          );
          templatePromise = EmailTemplateService.getTemplate(templateName).catch(() => null);
        }

        const [varsRes, overrideRes, templateRes] = await Promise.all([
          varsPromise,
          overridePromise,
          templatePromise,
        ]);

        if (!mounted) return;

        // variables
        const activeVars = (varsRes?.data || []).filter((v) => v.is_active);
        setVariables(activeVars);
        const map = {};
        activeVars.forEach((v) => (map[v.key] = v.id));
        setVariableKeyToId(map);
        setHasVariables(activeVars.length > 0);

        // template / override handling
        if (!templateName) {
          setTemplateDesc("Create a new email template");
          setSubject("");
          setBody("");
          setIsOverride(false);
        } else {
          if (overrideRes?.data) {
            const d = overrideRes.data;
            setSubject(d.subject || "");
            setBody(htmlToText(d.body_html || ""));
            setTemplateDesc(d.base_template_name || templateName);
            setIsOverride(d.is_override === true || !!d.is_override);
          } else if (templateRes?.data) {
            const d = templateRes.data;
            setSubject(d.subject || "");
            setBody(d.body || ""); // Global templates are plain text
            setTemplateDesc(d.name || templateName);
            setIsOverride(false);
          } else {
            toast.error("Failed to load template", { autoClose: 3000 });
          }
        }
      } catch (err) {
        console.error(err);
        toast.error("Failed to load data", { autoClose: 3000 });
        setHasVariables(false);
      } finally {
        if (mounted) setLoadingVariables(false);
      }
    };

    loadAll();

    return () => {
      mounted = false;
    };
  }, [templateName, htmlToText]);

  // -----------------------
  // Preview
  // -----------------------
  const handlePreview = () => {
    const bodyHtml = convertToHtmlForPreview(body);

    const sampleContext = {};
    (variables || []).forEach((v) => {
      sampleContext[v.key] = v.sample_value || `{{${v.key}}}`;
    });

    let renderedSubject = subject || "";
    let renderedBody = bodyHtml || "";

    Object.keys(sampleContext).forEach((key) => {
      // support spaces inside curly braces: {{ key }}
      const regex = new RegExp(`{{\\s*${key}\\s*}}`, "g");
      renderedSubject = renderedSubject.replace(regex, sampleContext[key]);
      renderedBody = renderedBody.replace(regex, `<strong>${sampleContext[key]}</strong>`);
    });

    const html = `
      <!DOCTYPE html>
      <html><head><meta charset="utf-8">
      <style>
        body { font-family: Arial, sans-serif; padding: 20px; background: #f9f9f9; }
        .email { background: white; padding: 30px; border-radius: 8px; max-width: 600px; margin: 0 auto; box-shadow: 0 2px 10px rgba(0,0,0,0.1); }
        p { line-height: 1.7; }
      </style>
      </head>
      <body>
        <div class="email">
          <h2>${renderedSubject}</h2>
          <div>${renderedBody}</div>
          <hr style="margin: 40px 0; border: 1px solid #eee;">
          <p><em>This is a preview. Variables will be replaced with real data when sent.</em></p>
        </div>
      </body></html>
    `;
    const win = window.open();
    if (win) {
      win.document.write(html);
      win.document.close();
    } else {
      toast.error("Unable to open preview window", { autoClose: 3000 });
    }
  };

  // -----------------------
  // Save / Send / Reset flows
  // -----------------------
  const handleSave = async () => {
    if (!templateName) return toast.error("No template name", { autoClose: 3000 });

    const bodyHtml = `<div style="font-family: Arial, sans-serif; line-height: 1.7; color: #333;">
      ${textToHtml(body)}
    </div>`;

    const payload = { subject, body_html: bodyHtml };

    try {
      await EmailTemplateService.updateCompanyOverride(templateName, payload);
      toast.success("Saved!", { autoClose: 2000 });
      // reload to show override state consistently
      window.location.reload();
    } catch (err) {
      console.error(err);
      toast.error("Save failed", { autoClose: 5000 });
    }
  };

  const handleEmail = async () => {
    if (!testEmail.trim()) {
      return toast.error("Please enter a recipient email", { position: "top-right", autoClose: 3000 });
    }

    if (!templateName) {
      return toast.error("No template selected", { position: "top-right", autoClose: 3000 });
    }

    // Save override first (best-effort)
    const bodyHtml = `<div style="font-family: Arial, sans-serif; line-height: 1.7; color: #333;">
      ${textToHtml(body)}
    </div>`;

    const savePayload = { subject, body_html: bodyHtml, invoice_number: invoiceNo, party_name: partyName };

    try {
      // attempt to save override but do not block send on save-failure: user preferred previously blocked; keep same behavior as earlier (you previously blocked)
      await EmailTemplateService.updateCompanyOverride(templateName, savePayload);
    } catch (err) {
      return toast.error("Failed to save template. Email not sent.", {
        position: "top-right",
        autoClose: 5000,
      });
    }

    const sendPayload = {
      recipient: testEmail.trim(),
      invoice_number: invoiceNo,
      party_name: partyName,
      ticket_id: ticketId,
    };

    try {
      await EmailTemplateService.sendTestEmail(templateName, sendPayload);
      toast.success(`Email sent to ${testEmail.trim()}`, {
        position: "top-right",
        autoClose: 4000,
      });
      setTestEmail("");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Send failed";
      toast.error(msg, { position: "top-right", autoClose: 5000 });
    }
  };

  const handleReset = async () => {
    if (!templateName || !isOverride) return;

    try {
      await EmailTemplateService.deleteCompanyOverride(templateName);
      toast.success("Template Reset Successfully", {
        position: "top-right",
        autoClose: 3000,
      });
      window.location.reload();
    } catch (err) {
      console.error(err);
      toast.error("Failed to reset", {
        position: "top-right",
        autoClose: 3000,
      });
    }
  };

  const handleCancel = () => navigate(-1);

  // -----------------------
  // Render
  // -----------------------
  return (
    <div className="page-wrapper">
      <div className="content container-fluid pt-3">
        <div className="row">
          <div className="col-md-12">
            <div className="card">
              <div className="card-body">
                <div className="row">
                  {/* LEFT: Editor */}
                  <div className="col-xl-8">
                    <h5>{t("Email Template Editor")}</h5>
                    <div className="mb-3">
                      <h6>{templateName || "New Template"}</h6>
                    </div>

                    {/* Action buttons */}
                    <div className="d-flex flex-wrap gap-2 mb-3">
                      <button className="btn btn-outline-secondary btn-sm" onClick={handlePreview}>
                        {t("Preview")}
                      </button>
                      {isOverride && (
                        <button className="btn btn-outline-secondary btn-sm" onClick={handleReset}>
                          {t("Reset")}
                        </button>
                      )}
                      <button className="btn btn-outline-secondary btn-sm" onClick={handleCancel}>
                        {t("Cancel")}
                      </button>
                      <button className="btn btn-outline-secondary btn-sm" onClick={handleEmail}>
                        {t("Send")}
                      </button>
                      <button className="btn btn-primary btn-sm" onClick={handleSave}>
                        {t("Save")}
                      </button>
                    </div>

                    {/* TO INPUT */}
                    <div className="mb-3">
                      <label className="col-form-label">{t("To")}</label>
                      <input
                        type="email"
                        className="form-control"
                        placeholder="recipient@example.com"
                        value={testEmail}
                        onChange={(e) => setTestEmail(e.target.value)}
                      />
                    </div>
                    {supportTemplate && (
                    <div className="mb-3">
                      <label className="col-form-label">Ticket ID</label>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="ticket ID"
                        value={ticketId}
                        onChange={(e) => setTicketId(e.target.value)}
                      />
                    </div>
                    )}

                    {isInvoiceTemplate && (
                      <div className="mb-3">
                        <label className="col-form-label">Invoice No</label>
                        <input
                          type="text"
                          className="form-control"
                          placeholder="invoice number"
                          value={invoiceNo}
                          onChange={(e) => setInvoiceNo(e.target.value)}
                        />
                      </div>
                    )}

                    {isPasswordTemplate && (
                      <div className="mb-3">
                        <label className="col-form-label">Party Name</label>
                        <input
                          type="text"
                          className="form-control"
                          placeholder="party name"
                          value={partyName}
                          onChange={(e) => setPartyName(e.target.value)}
                        />
                      </div>
                    )}

                    {/* Subject */}
                    <div className="mb-3">
                      <label className="col-form-label">{t("Subject")}</label>
                      <input
                        ref={subjectRef}
                        type="text"
                        className="form-control"
                        value={subject}
                        onChange={(e) => setSubject(e.target.value)}
                        onFocus={() => setFocusedField("subject")}
                      />
                    </div>

                    {/* Body */}
                    <div className="mb-3">
                      <label className="col-form-label">{t("Body")}</label>
                      <textarea
                        ref={bodyRef}
                        className="textareaticket form-control"
                        rows="4"
                        cols="50"
                        value={body || ""}
                        onChange={(e) => setBody(e.target.value)}
                        onFocus={() => setFocusedField("body")}
                        style={{
                          fontFamily: "monospace",
                          fontSize: "13px",
                          height: "250px",
                        }}
                      />
                    </div>
                  </div>

                  {/* RIGHT: Variables */}
                  <div className="col-xl-4">
                    <div className="border-start ps-4">
                      <h5>{t("Dynamic Variables")}</h5>

                      <div
                        style={{
                          maxHeight: "650px",
                          overflowY: "auto",
                          paddingRight: "5px",
                        }}
                      >
                        <div className="row g-2 mt-2">
                          {loadingVariables ? (
                            <div className="text-center text-muted small">Loading variables...</div>
                          ) : variables.length === 0 ? (
                            <div className="text-center text-muted small">No variables available</div>
                          ) : (
                            variables.map((v) => (
                              <div key={v.id} className="col-12">
                                <div className="card p-2">
                                  <div className="text-muted" style={{ fontSize: "12px" }}>
                                    {v.sample_value || "—"}
                                  </div>
                                  <div className="mt-2 d-flex gap-1">
                                    <button
                                      className="btn btn-sm btn-outline-secondary"
                                      onClick={() => handleInsert(v.key)}
                                    >
                                      Insert
                                    </button>
                                    <button
                                      className="btn btn-sm btn-outline-secondary"
                                      onClick={() => handleCopy(v.key)}
                                    >
                                      Copy
                                    </button>
                                  </div>
                                </div>
                              </div>
                            ))
                          )}
                        </div>
                      </div>

                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <ToastContainer />
    </div>
  );
}
